import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import { Plus, Edit, Trash2, X, RefreshCw, Languages, Loader2, MapPin } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL from relative or absolute path
const getImageUrl = (url) => {
  if (!url) return '';
  // If it's already a full URL, return as is
  if (url.startsWith('http')) return url;
  // If it's a relative path, prepend BACKEND_URL
  return `${BACKEND_URL}${url}`;
};

export const AdminApartmentsPage = () => {
  const { getAuthHeaders, language, convertPrice } = useApp();
  const [apartments, setApartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [exchangeRate, setExchangeRate] = useState({ rate: 4.97, date: '', source: '' });
  const [autoConvert, setAutoConvert] = useState(true);
  const [translating, setTranslating] = useState(false);
  const [generatingMap, setGeneratingMap] = useState(false);
  const [formData, setFormData] = useState({
    title: { ro: '', en: '', fr: '' },
    description: { ro: '', en: '', fr: '' },
    price_eur: '',
    price_ron: '',
    status: 'available',
    available_in_months: 0,
    apartment_type: '',
    area: '',
    images: [],
    features: [],
    categories: [],
    labels: [],
    external_link: '',
    map_embed: '',
    map_address: '',  // Public address for map
    map_lat: '',      // Latitude for manual input
    map_lon: '',      // Longitude for manual input
    real_address: '',
  });
  const [imageInput, setImageInput] = useState('');
  const [featureInput, setFeatureInput] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchApartments();
    fetchCategories();
    fetchExchangeRate();
  }, []);

  const fetchExchangeRate = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/exchange-rate`);
      setExchangeRate(data);
    } catch (error) {
      console.error('Failed to fetch exchange rate');
    }
  };

  const handleEurChange = (value) => {
    setFormData({ ...formData, price_eur: value });
    if (autoConvert && value && exchangeRate.rate) {
      const ronValue = Math.round(parseFloat(value) * exchangeRate.rate);
      setFormData(prev => ({ ...prev, price_eur: value, price_ron: ronValue.toString() }));
    }
  };

  // Auto-translate function
  const handleTranslate = async () => {
    if (!formData.title.ro && !formData.description.ro) {
      toast.error('Completează titlul și descrierea în română înainte de traducere');
      return;
    }

    setTranslating(true);
    try {
      // Translate title
      if (formData.title.ro) {
        const { data: titleTranslations } = await axios.post(
          `${BACKEND_URL}/api/translate`,
          { text: formData.title.ro, source_lang: 'ro', target_langs: ['en', 'fr'] },
          { headers: getAuthHeaders() }
        );
        setFormData(prev => ({
          ...prev,
          title: {
            ...prev.title,
            en: titleTranslations.en || prev.title.en,
            fr: titleTranslations.fr || prev.title.fr
          }
        }));
      }

      // Translate description
      if (formData.description.ro) {
        const { data: descTranslations } = await axios.post(
          `${BACKEND_URL}/api/translate`,
          { text: formData.description.ro, source_lang: 'ro', target_langs: ['en', 'fr'] },
          { headers: getAuthHeaders() }
        );
        setFormData(prev => ({
          ...prev,
          description: {
            ...prev.description,
            en: descTranslations.en || prev.description.en,
            fr: descTranslations.fr || prev.description.fr
          }
        }));
      }

      toast.success('Traducere completă! Verifică și corectează dacă este nevoie.');
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('Eroare la traducere: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTranslating(false);
    }
  };

  const fetchApartments = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/apartments`);
      setApartments(data);
    } catch (error) {
      toast.error('Failed to fetch apartments');
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/categories`);
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        price_eur: parseFloat(formData.price_eur),
        price_ron: parseFloat(formData.price_ron),
      };

      console.log('Saving apartment with images:', payload.images);

      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/apartments/${editingId}`, payload, {
          headers: getAuthHeaders(),
        });
        toast.success('Apartment updated!');
      } else {
        await axios.post(`${BACKEND_URL}/api/apartments`, payload, {
          headers: getAuthHeaders(),
        });
        toast.success('Apartment created!');
      }

      setShowDialog(false);
      resetForm();
      fetchApartments();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save apartment: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (apartment) => {
    setEditingId(apartment.id);
    setFormData({
      title: apartment.title,
      description: apartment.description,
      price_eur: apartment.price_eur.toString(),
      price_ron: apartment.price_ron.toString(),
      status: apartment.status,
      available_in_months: apartment.available_in_months || 0,
      apartment_type: apartment.apartment_type || '',
      area: apartment.area || '',
      images: apartment.images || [],
      features: apartment.features || [],
      categories: apartment.categories || [],
      labels: apartment.labels || [],
      external_link: apartment.external_link || '',
      map_embed: apartment.map_embed || '',
      map_address: apartment.map_address || '',
      map_lat: apartment.map_lat || '',
      map_lon: apartment.map_lon || '',
      real_address: apartment.real_address || '',
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this apartment?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/apartments/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success('Apartment deleted!');
      fetchApartments();
    } catch (error) {
      toast.error('Failed to delete apartment');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      title: { ro: '', en: '', fr: '' },
      description: { ro: '', en: '', fr: '' },
      price_eur: '',
      price_ron: '',
      status: 'available',
      available_in_months: 0,
      apartment_type: '',
      area: '',
      images: [],
      features: [],
      categories: [],
      labels: [],
      external_link: '',
      map_embed: '',
      map_address: '',
      map_lat: '',
      map_lon: '',
      real_address: '',
    });
    setImageInput('');
    setFeatureInput('');
  };

  // Generate OpenStreetMap embed from address
  const generateMapFromAddress = async () => {
    if (!formData.map_address) {
      toast.error('Introduceți o adresă pentru hartă');
      return;
    }
    
    setGeneratingMap(true);
    try {
      // Try backend geocoding first
      try {
        const { data } = await axios.post(`${BACKEND_URL}/api/geocode`, {
          address: formData.map_address
        }, { headers: getAuthHeaders() });
        
        if (data.success && data.lat && data.lon) {
          const lat = data.lat;
          const lon = data.lon;
          const mapEmbed = `<iframe 
            width="100%" 
            height="400" 
            frameborder="0" 
            scrolling="no" 
            marginheight="0" 
            marginwidth="0" 
            src="https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(lon)-0.01}%2C${parseFloat(lat)-0.01}%2C${parseFloat(lon)+0.01}%2C${parseFloat(lat)+0.01}&layer=mapnik&marker=${lat}%2C${lon}"
            style="border: 1px solid #ccc; border-radius: 8px;">
          </iframe>`;
          
          setFormData({ ...formData, map_embed: mapEmbed });
          toast.success(`Hartă generată: ${data.display_name?.substring(0, 50) || formData.map_address}...`);
          return;
        }
      } catch (backendError) {
        console.log('Backend geocoding failed, using search embed:', backendError);
      }
      
      // Fallback: Generate a search-based map embed
      // This shows a map centered on the search term
      const searchQuery = encodeURIComponent(formData.map_address + ', Romania');
      const mapEmbed = `<iframe 
        width="100%" 
        height="400" 
        frameborder="0" 
        scrolling="no" 
        marginheight="0" 
        marginwidth="0" 
        src="https://www.openstreetmap.org/export/embed.html?bbox=23.5%2C43.5%2C30%2C48.5&layer=mapnik"
        style="border: 1px solid #ccc; border-radius: 8px;">
      </iframe>
      <p style="text-align:center; margin-top:8px; font-size:12px; color:#666;">
        📍 Adresă: ${formData.map_address}
        <br><a href="https://www.openstreetmap.org/search?query=${searchQuery}" target="_blank" style="color:#1e40af;">
          Deschide în OpenStreetMap →
        </a>
      </p>`;
      
      setFormData({ ...formData, map_embed: mapEmbed });
      toast.success('Hartă generată! Pentru precizie mai mare, copiați coordonatele de pe OpenStreetMap.');
      
    } catch (error) {
      console.error('Map generation error:', error);
      toast.error('Eroare la generarea hărții');
    } finally {
      setGeneratingMap(false);
    }
  };

  const addImage = () => {
    if (imageInput.trim()) {
      setFormData({ ...formData, images: [...formData.images, imageInput.trim()] });
      setImageInput('');
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, or WebP images.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploadingImage(true);
    console.log('Uploading file:', file.name, file.type, file.size);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${BACKEND_URL}/api/upload/image`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });

      // Use the full URL returned from backend
      const imageUrl = response.data.url;
      console.log('Upload successful, image URL:', imageUrl);
      
      const newImages = [...formData.images, imageUrl];
      console.log('New images array:', newImages);
      
      setFormData({ ...formData, images: newImages });
      toast.success('Image uploaded successfully! Click Update to save.');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingImage(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const removeImage = (index) => {
    setFormData({ ...formData, images: formData.images.filter((_, i) => i !== index) });
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData({ ...formData, features: [...formData.features, featureInput.trim()] });
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setFormData({ ...formData, features: formData.features.filter((_, i) => i !== index) });
  };

  const toggleLabel = (label) => {
    if (formData.labels.includes(label)) {
      setFormData({ ...formData, labels: formData.labels.filter((l) => l !== label) });
    } else {
      setFormData({ ...formData, labels: [...formData.labels, label] });
    }
  };

  const getTranslatedText = (textObj) => {
    return textObj?.[language] || textObj?.en || '';
  };

  return (
    <AdminLayout>
      <div data-testid="admin-apartments-page">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-serif">Apartments</h1>
          <Button
            onClick={() => {
              resetForm();
              setShowDialog(true);
            }}
            data-testid="add-apartment-button"
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus size={20} className="mr-2" />
            Add Apartment
          </Button>
        </div>

        {/* Apartments List */}
        <div className="space-y-4">
          {apartments.map((apartment) => (
            <div key={apartment.id} data-testid={`apartment-item-${apartment.id}`} className="glass p-6">
              <div className="flex items-start gap-6">
                {apartment.images?.[0] && (
                  <img src={getImageUrl(apartment.images[0])} alt="" className="w-32 h-32 object-cover" />
                )}
                <div className="flex-1">
                  <h3 className="text-xl font-serif mb-2">{getTranslatedText(apartment.title)}</h3>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {getTranslatedText(apartment.description)}
                  </p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-primary font-semibold">
                      {convertPrice(apartment.price_eur, apartment.price_ron)}
                    </span>
                    <span
                      className={`px-2 py-1 text-xs uppercase ${
                        apartment.status === 'available' 
                          ? 'bg-green-500/20 text-green-400' 
                          : apartment.status === 'available_soon'
                            ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {apartment.status === 'available_soon' 
                        ? `Available in ${apartment.available_in_months || 1} ${(apartment.available_in_months || 1) === 1 ? 'month' : 'months'}`
                        : apartment.status}
                    </span>
                    {apartment.labels?.map((label) => (
                      <span key={label} className="px-2 py-1 bg-primary/20 text-primary text-xs uppercase">
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleEdit(apartment)}
                    data-testid={`edit-apartment-${apartment.id}`}
                    variant="outline"
                    size="sm"
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    onClick={() => handleDelete(apartment.id)}
                    data-testid={`delete-apartment-${apartment.id}`}
                    variant="destructive"
                    size="sm"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-950 border-white/10">
            <DialogHeader>
              <DialogTitle className="text-2xl font-serif">
                {editingId ? 'Edit Apartment' : 'Add Apartment'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Title */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-wider text-primary">Title</label>
                  <Button
                    type="button"
                    onClick={handleTranslate}
                    disabled={translating || (!formData.title.ro && !formData.description.ro)}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {translating ? (
                      <>
                        <Loader2 size={14} className="mr-2 animate-spin" />
                        Se traduce...
                      </>
                    ) : (
                      <>
                        <Languages size={14} className="mr-2" />
                        Auto-Traducere EN/FR
                      </>
                    )}
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <span className="text-xs text-muted-foreground mb-1 block">🇷🇴 Română (obligatoriu)</span>
                    <input
                      type="text"
                      placeholder="Titlu în română..."
                      value={formData.title.ro}
                      onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ro: e.target.value } })}
                      required
                      className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground mb-1 block">🇬🇧 English (auto/editabil)</span>
                    <input
                      type="text"
                      placeholder="English title..."
                      value={formData.title.en}
                      onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                      required
                      className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground mb-1 block">🇫🇷 Français (auto/editabil)</span>
                    <input
                      type="text"
                      placeholder="Titre en français..."
                      value={formData.title.fr}
                      onChange={(e) => setFormData({ ...formData, title: { ...formData.title, fr: e.target.value } })}
                      required
                      className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Description</label>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <span className="text-xs text-muted-foreground mb-1 block">🇷🇴 Română (obligatoriu)</span>
                    <textarea
                      placeholder="Descriere în română..."
                      value={formData.description.ro}
                      onChange={(e) =>
                        setFormData({ ...formData, description: { ...formData.description, ro: e.target.value } })
                      }
                      required
                      rows={3}
                      className="bg-transparent border border-white/20 p-3 text-sm focus:outline-none focus:border-primary resize-none w-full"
                    />
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground mb-1 block">🇬🇧 English (auto/editabil)</span>
                    <textarea
                      placeholder="English description..."
                      value={formData.description.en}
                      onChange={(e) =>
                        setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })
                      }
                      required
                      rows={3}
                      className="bg-transparent border border-white/20 p-3 text-sm focus:outline-none focus:border-primary resize-none w-full"
                    />
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground mb-1 block">🇫🇷 Français (auto/editabil)</span>
                    <textarea
                      placeholder="Description en français..."
                      value={formData.description.fr}
                      onChange={(e) =>
                        setFormData({ ...formData, description: { ...formData.description, fr: e.target.value } })
                      }
                      required
                      rows={3}
                      className="bg-transparent border border-white/20 p-3 text-sm focus:outline-none focus:border-primary resize-none w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Type & Area */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Apartment Type</label>
                  <select
                    value={formData.apartment_type}
                    onChange={(e) => setFormData({ ...formData, apartment_type: e.target.value })}
                    className="w-full bg-slate-800 border border-white/20 p-3 text-sm focus:outline-none focus:border-primary"
                  >
                    <option value="">-- Select Type --</option>
                    <option value="studio">Studio</option>
                    <option value="1_bedroom">1 Bedroom</option>
                    <option value="2_bedroom">2 Bedrooms</option>
                    <option value="3_bedroom">3 Bedrooms</option>
                    <option value="apartment">Apartment</option>
                    <option value="house">House</option>
                    <option value="penthouse">Penthouse</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Area / Zone</label>
                  <select
                    value={formData.area}
                    onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                    className="w-full bg-slate-800 border border-white/20 p-3 text-sm focus:outline-none focus:border-primary"
                  >
                    <option value="">-- Select Area --</option>
                    {categories
                      .filter((c) => c.type === 'area')
                      .map((cat) => {
                        const areaName = cat.name?.[language] || cat.name?.en || '';
                        return (
                          <option key={cat.id} value={areaName}>
                            {areaName}
                          </option>
                        );
                      })}
                  </select>
                </div>
              </div>

              {/* Prices */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs uppercase tracking-wider text-primary">Prices</label>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      BNR: 1 EUR = {exchangeRate.rate} RON
                      {exchangeRate.date && <span className="text-primary ml-1">({exchangeRate.date})</span>}
                    </span>
                    <button
                      type="button"
                      onClick={fetchExchangeRate}
                      className="text-primary hover:text-primary/80 transition-colors"
                      title="Refresh exchange rate"
                    >
                      <RefreshCw size={14} />
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    id="autoConvert"
                    checked={autoConvert}
                    onChange={(e) => setAutoConvert(e.target.checked)}
                    className="w-4 h-4 accent-primary"
                  />
                  <label htmlFor="autoConvert" className="text-xs text-muted-foreground">
                    Auto-convert EUR → RON using BNR rate
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Price EUR €</label>
                    <input
                      type="number"
                      value={formData.price_eur}
                      onChange={(e) => handleEurChange(e.target.value)}
                      required
                      placeholder="Enter EUR price"
                      className="w-full bg-transparent border border-white/20 px-3 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">
                      Price RON Lei {autoConvert && <span className="text-primary">(auto)</span>}
                    </label>
                    <input
                      type="number"
                      value={formData.price_ron}
                      onChange={(e) => setFormData({ ...formData, price_ron: e.target.value })}
                      required
                      placeholder={autoConvert ? "Auto-calculated" : "Enter RON price"}
                      className={`w-full bg-transparent border px-3 py-2 text-sm focus:outline-none focus:border-primary ${
                        autoConvert ? 'border-primary/30 text-primary' : 'border-white/20'
                      }`}
                    />
                  </div>
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Status</label>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, status: 'available', available_in_months: 0 })}
                    className={`px-6 py-3 text-sm font-semibold uppercase transition-all rounded ${
                      formData.status === 'available'
                        ? 'bg-green-500 text-white shadow-lg shadow-green-500/30'
                        : 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                    }`}
                  >
                    ✓ Available
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, status: 'occupied', available_in_months: 0 })}
                    className={`px-6 py-3 text-sm font-semibold uppercase transition-all rounded ${
                      formData.status === 'occupied'
                        ? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
                        : 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                    }`}
                  >
                    ✗ Occupied
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, status: 'available_soon', available_in_months: formData.available_in_months || 1 })}
                    className={`px-6 py-3 text-sm font-semibold uppercase transition-all rounded ${
                      formData.status === 'available_soon'
                        ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
                        : 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                    }`}
                  >
                    🕐 Available Soon
                  </button>
                </div>
                
                {/* Available in X months selector */}
                {formData.status === 'available_soon' && (
                  <div className="mt-3 p-3 bg-amber-900/20 border border-amber-500/30 rounded">
                    <label className="text-xs text-amber-300 mb-2 block">Disponibil în câte luni?</label>
                    <div className="flex flex-wrap gap-2">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(month => (
                        <button
                          key={month}
                          type="button"
                          onClick={() => setFormData({ ...formData, available_in_months: month })}
                          className={`w-10 h-10 text-sm font-bold rounded transition-all ${
                            formData.available_in_months === month
                              ? 'bg-amber-500 text-white'
                              : 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                          }`}
                        >
                          {month}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-amber-400/70 mt-2">
                      Va afișa: "Available in {formData.available_in_months || 1} {(formData.available_in_months || 1) === 1 ? 'month' : 'months'}"
                    </p>
                  </div>
                )}
              </div>

              {/* Labels */}
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Labels</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => toggleLabel('promo')}
                    className={`px-5 py-3 text-sm font-bold uppercase transition-all rounded ${
                      formData.labels.includes('promo')
                        ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
                        : 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                    }`}
                  >
                    🏷️ PROMO
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleLabel('recommended')}
                    className={`px-5 py-3 text-sm font-bold uppercase transition-all rounded ${
                      formData.labels.includes('recommended')
                        ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                        : 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                    }`}
                  >
                    ⭐ RECOMMENDED
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleLabel('new')}
                    className={`px-5 py-3 text-sm font-bold uppercase transition-all rounded ${
                      formData.labels.includes('new')
                        ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                        : 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                    }`}
                  >
                    🆕 NEW
                  </button>
                </div>
              </div>

              {/* Images */}
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Images</label>
                
                {/* Upload from file */}
                <div className="mb-3 p-4 bg-slate-900/50 border border-white/10 rounded">
                  <label className="text-xs text-muted-foreground mb-2 block">Upload Image File</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      className="flex-1 text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer"
                    />
                    {uploadingImage && <span className="text-xs text-muted-foreground">Uploading...</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Max 5MB • JPG, PNG, GIF, WebP</p>
                </div>

                {/* Add by URL */}
                <div className="mb-3">
                  <label className="text-xs text-muted-foreground mb-2 block">Or Add Image URL</label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      placeholder="https://example.com/image.jpg"
                      value={imageInput}
                      onChange={(e) => setImageInput(e.target.value)}
                      className="flex-1 bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                    <Button type="button" onClick={addImage} size="sm">
                      Add
                    </Button>
                  </div>
                  <p className="text-xs text-yellow-500 mt-1">⚠️ Use HTTPS URLs only. HTTP images may not display due to browser security.</p>
                </div>

                {/* Image gallery */}
                <div className="grid grid-cols-4 gap-3">
                  {formData.images.map((img, index) => (
                    <div key={index} className="relative group">
                      <img src={getImageUrl(img)} alt="" className="w-full h-24 object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Features */}
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Features</label>
                
                {/* Predefined features from categories */}
                {categories.filter(c => c.type === 'feature').length > 0 && (
                  <div className="mb-4">
                    <label className="text-xs text-muted-foreground mb-2 block">Select from predefined features:</label>
                    <div className="flex flex-wrap gap-2">
                      {categories
                        .filter((c) => c.type === 'feature')
                        .map((category) => {
                          const featureName = category.name?.[language] || category.name?.en || '';
                          const isSelected = formData.features.includes(featureName);
                          return (
                            <button
                              key={category.id}
                              type="button"
                              onClick={() => {
                                if (isSelected) {
                                  setFormData({
                                    ...formData,
                                    features: formData.features.filter((f) => f !== featureName),
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    features: [...formData.features, featureName],
                                  });
                                }
                              }}
                              className={`px-4 py-2 text-xs uppercase transition-colors ${
                                isSelected
                                  ? 'bg-primary text-primary-foreground'
                                  : 'border border-white/20 text-foreground hover:bg-white/5'
                              }`}
                            >
                              {featureName}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Manual feature input */}
                <div className="mb-3">
                  <label className="text-xs text-muted-foreground mb-2 block">Or add custom feature:</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Custom feature name"
                      value={featureInput}
                      onChange={(e) => setFeatureInput(e.target.value)}
                      className="flex-1 bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                    <Button type="button" onClick={addFeature} size="sm">
                      Add
                    </Button>
                  </div>
                </div>

                {/* Selected features display */}
                {formData.features.length > 0 && (
                  <div>
                    <label className="text-xs text-muted-foreground mb-2 block">Selected features:</label>
                    <div className="flex flex-wrap gap-2">
                      {formData.features.map((feature, index) => (
                        <span key={index} className="px-3 py-1 bg-primary/20 text-primary text-xs flex items-center gap-2">
                          {feature}
                          <button type="button" onClick={() => removeFeature(index)}>
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* External Link */}
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">
                  External Link (Airbnb/Booking)
                </label>
                <input
                  type="url"
                  value={formData.external_link}
                  onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
                />
              </div>

              {/* Real Address - Hidden from public site */}
              <div className="p-4 bg-amber-900/20 border border-amber-500/30 rounded">
                <label className="text-xs uppercase tracking-wider text-amber-400 mb-2 block flex items-center gap-2">
                  🔒 Adresă Reală (pentru contract - NU apare pe site)
                </label>
                <input
                  type="text"
                  placeholder="Str. Exemplu, nr. 10, bl. A, sc. B, et. 3, ap. 15, Iași"
                  value={formData.real_address}
                  onChange={(e) => setFormData({ ...formData, real_address: e.target.value })}
                  className="w-full bg-transparent border border-amber-500/30 p-3 text-sm focus:outline-none focus:border-amber-500"
                />
                <p className="text-xs text-amber-500/70 mt-2">⚠️ Această adresă este privată și va fi folosită doar pentru contracte.</p>
              </div>

              {/* Map Address - Public */}
              <div className="bg-blue-900/20 border border-blue-500/30 p-4 rounded">
                <label className="text-sm font-semibold mb-2 text-blue-300 flex items-center gap-2">
                  <MapPin size={16} /> 🗺️ Adresă Hartă (publică - apare pe site)
                </label>
                <div className="flex gap-2 mt-2">
                  <input
                    type="text"
                    placeholder="Ex: Piața Unirii, Iași, România"
                    value={formData.map_address}
                    onChange={(e) => setFormData({ ...formData, map_address: e.target.value })}
                    className="flex-1 bg-transparent border border-blue-500/30 p-3 text-sm focus:outline-none focus:border-blue-500"
                  />
                  <Button 
                    type="button" 
                    onClick={generateMapFromAddress} 
                    disabled={generatingMap || !formData.map_address}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {generatingMap ? <Loader2 size={16} className="animate-spin" /> : <MapPin size={16} />}
                    <span className="ml-2">Generează Hartă</span>
                  </Button>
                </div>
                <p className="text-xs text-blue-400/70 mt-2">📍 Introduceți o adresă și apăsați "Generează Hartă". Pentru precizie maximă, puteți introduce coordonatele manual mai jos.</p>
                
                {/* Manual coordinates input */}
                <div className="mt-3 flex gap-2">
                  <div className="flex-1">
                    <label className="text-xs text-gray-400 mb-1 block">Latitudine (ex: 47.1585)</label>
                    <input
                      type="text"
                      placeholder="47.1585"
                      value={formData.map_lat || ''}
                      onChange={(e) => setFormData({ ...formData, map_lat: e.target.value })}
                      className="w-full bg-transparent border border-gray-600 p-2 text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-gray-400 mb-1 block">Longitudine (ex: 27.6014)</label>
                    <input
                      type="text"
                      placeholder="27.6014"
                      value={formData.map_lon || ''}
                      onChange={(e) => setFormData({ ...formData, map_lon: e.target.value })}
                      className="w-full bg-transparent border border-gray-600 p-2 text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <Button 
                    type="button" 
                    onClick={() => {
                      if (formData.map_lat && formData.map_lon) {
                        const lat = parseFloat(formData.map_lat);
                        const lon = parseFloat(formData.map_lon);
                        if (!isNaN(lat) && !isNaN(lon)) {
                          const mapEmbed = `<iframe width="100%" height="400" frameborder="0" scrolling="no" src="https://www.openstreetmap.org/export/embed.html?bbox=${lon-0.01}%2C${lat-0.01}%2C${lon+0.01}%2C${lat+0.01}&layer=mapnik&marker=${lat}%2C${lon}" style="border: 1px solid #ccc; border-radius: 8px;"></iframe>`;
                          setFormData({ ...formData, map_embed: mapEmbed });
                          toast.success('Hartă generată din coordonate!');
                        } else {
                          toast.error('Coordonate invalide');
                        }
                      }
                    }}
                    disabled={!formData.map_lat || !formData.map_lon}
                    variant="outline"
                    className="self-end"
                  >
                    Aplică
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  💡 Găsiți coordonatele pe <a href="https://www.openstreetmap.org" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">OpenStreetMap.org</a> - click dreapta pe hartă → "Show address"
                </p>
                
                {formData.map_embed && (
                  <div className="mt-4">
                    <label className="text-xs text-green-400 block mb-2">✅ Hartă generată - Preview:</label>
                    <div className="h-48 border border-green-500/30 rounded overflow-hidden" dangerouslySetInnerHTML={{ __html: formData.map_embed }} />
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="flex gap-3">
                <Button type="submit" disabled={loading} className="flex-1 bg-primary text-primary-foreground">
                  {loading ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </Button>
                <Button type="button" onClick={() => setShowDialog(false)} variant="outline">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminApartmentsPage;